import React from 'react'
import { PiUser } from 'react-icons/pi'
import { useUserOptions } from 'services/users'
import { useAuth } from 'context/authentication/useAuth'
import { FilterStore } from 'components/Filters/FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
  ds2026DropdownSeparatorClass,
} from 'components/FbUI/ds2026DropdownMenu'
import FbButton from 'components/FbUI/FbButton'
import Checkbox from 'components/Checkbox/checkbox'
import { cn } from 'components/UI/cn'
import {
  boardFilterBadgeClass,
  boardFilterTriggerClass,
} from 'components/Deals/boardToolbarStyles'
import { QuickFilterSearch } from 'components/Deals/QuickFilterSearch'
import { useToolbarDensity } from 'components/Tables/CommonTableCells/ToolbarDensityContext'

/** Filter store key the account-owner filter commits under. Mirrors the key the
 *  shared deals filterset (DealsFiltersetV2) uses for `account_owner`, and the
 *  key `buildFilterParams` serializes into the `account_owner` query param. */
const OWNER_FILTER_KEY = 'account_owner'

interface OwnerQuickFilterProps {
  /** The deals filter store (shared with the table). Selections commit here, so
   *  they flow into every board column query — and, by design, the table too. */
  filterStore: FilterStore
}

/**
 * Board-only "Owner" quick filter.
 *
 * Mirrors the Visible Stages dropdown: same trigger chrome, same checkbox-row
 * menu, and it auto-applies on every toggle (no Apply/Cancel). A pinned "My
 * deals" row scopes to the current user in one click — the common case on the
 * board. This writes the same shared `account_owner` store value the "More
 * Filters" modal's owner filter uses, so the two stay in sync (redundant but
 * consistent); this is just the one-click path to the most common scope.
 *
 * Selection is tracked in local state (seeded from the committed filter each
 * time the menu opens) so Select all ↔ Deselect all is a true toggle. "No
 * filter" (empty committed value) means every owner is shown, so the menu seeds
 * to all-checked by default. Every change commits straight to the shared
 * `account_owner` value — unlike the in-modal SelectFilter, which only stages
 * pending state for the modal's Apply button.
 */
export function OwnerQuickFilter({ filterStore }: OwnerQuickFilterProps) {
  const density = useToolbarDensity()
  const { user } = useAuth()
  const myId = user?.id != null ? String(user.id) : null
  const { data: ownerOptions } = useUserOptions({ onlyActive: true })

  const valueFilters = filterStore((s) => s.valueFilters)
  const setPendingValueFilters = filterStore((s) => s.setPendingValueFilters)
  const clearPendingValueFilter = filterStore((s) => s.clearPendingValueFilter)
  const clearValueFilter = filterStore((s) => s.clearValueFilter)
  const applyFilters = filterStore((s) => s.applyFilters)

  const owners = ownerOptions ?? []
  const allCount = owners.length
  const allIds = React.useMemo(
    () => owners.map((o) => String(o.value)),
    [owners]
  )

  // Committed owner selection (what's actually filtering right now). Empty means
  // "no filter" — i.e. all owners.
  const applied = valueFilters[OWNER_FILTER_KEY] ?? []
  const isFiltered = applied.length > 0
  const appliedIsMine =
    myId != null && applied.length === 1 && applied[0]?.value === myId

  // Local checkbox state — the source of truth for the menu while it's open, so
  // Select all ↔ Deselect all genuinely toggles. Seeded from the committed
  // value on open (no filter → all checked).
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [search, setSearch] = React.useState('')
  // Committed value captured when the menu opened. "Reset" reverts to it — an
  // in-session undo of any toggles made while the menu is open (and the natural
  // hook for "revert to default" once saved/default views exist). Stored as the
  // raw ValueFilter[] so restoring keeps labels (e.g. "My deals only", whose
  // owner may not be in the options list).
  const openSnapshotRef = React.useRef<{ value: string; label: string }[]>([])

  function seedFromCommitted() {
    openSnapshotRef.current = applied
    setSelected(
      isFiltered ? new Set(applied.map((f) => f.value)) : new Set(allIds)
    )
  }

  function clearOwnerFilter() {
    // Clear both pending and committed so a later applyFilters elsewhere can't
    // resurrect a stale owner selection.
    clearPendingValueFilter(OWNER_FILTER_KEY)
    clearValueFilter(OWNER_FILTER_KEY)
  }

  function setOwnerFilter(ids: Set<string>) {
    const values = owners
      .filter((o) => ids.has(String(o.value)))
      .map((o) => ({ value: String(o.value), label: o.label }))
    setPendingValueFilters(OWNER_FILTER_KEY, values)
    void applyFilters()
  }

  // Commit a selection and keep local state in sync. All-selected collapses to
  // "no filter" (cleaner than persisting every id); any other set commits as-is.
  function apply(next: Set<string>) {
    setSelected(next)
    if (next.size === allCount) {
      clearOwnerFilter()
    } else {
      setOwnerFilter(next)
    }
  }

  function toggleOwner(id: string) {
    const next = new Set(selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    apply(next)
  }

  const allSelected = allCount > 0 && selected.size === allCount
  const someSelected = selected.size > 0 && !allSelected

  function toggleSelectAll() {
    apply(allSelected ? new Set() : new Set(allIds))
  }

  // Reset reverts to the on-open snapshot. Compare the current selection against
  // the snapshot's effective set (empty committed value == all owners selected).
  const snapshot = openSnapshotRef.current
  const snapshotIds = snapshot.length
    ? new Set(snapshot.map((f) => f.value))
    : new Set(allIds)
  const isDirty =
    selected.size !== snapshotIds.size ||
    [...selected].some((id) => !snapshotIds.has(id))
  function resetSelection() {
    setSelected(new Set(snapshotIds))
    if (snapshot.length === 0) {
      clearOwnerFilter()
    } else {
      setPendingValueFilters(OWNER_FILTER_KEY, snapshot)
      void applyFilters()
    }
  }

  // "My deals" is checked when the selection is exactly the current user.
  // Toggling it on scopes to just them; toggling off returns to all owners.
  const isMineSelected =
    myId != null && selected.size === 1 && selected.has(myId)
  function toggleMine() {
    if (!myId) return
    if (isMineSelected) {
      apply(new Set(allIds)) // toggle off → all owners (no filter)
      return
    }
    // Commit directly from the authenticated user rather than via setOwnerFilter
    // (which resolves labels from the owner options): the current user often
    // isn't in the active-owner list, so a lookup would commit an empty value.
    const label =
      [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
      'My deals'
    setSelected(new Set([myId]))
    setPendingValueFilters(OWNER_FILTER_KEY, [{ value: myId, label }])
    void applyFilters()
  }

  const search_ = search.trim().toLowerCase()
  const visibleOwners = search_
    ? owners.filter((o) => o.label.toLowerCase().includes(search_))
    : owners

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          seedFromCommitted()
        } else {
          setSearch('')
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <FbButton
          variant="secondary"
          className={boardFilterTriggerClass(density)}
        >
          <PiUser size={16} />
          Owner
          <span className={boardFilterBadgeClass}>
            {!isFiltered ? 'All' : appliedIsMine ? 'My deals' : applied.length}
          </span>
        </FbButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(ds2026DropdownContentClass, 'max-h-80 overflow-y-auto')}
      >
        {/* Select all ↔ Deselect all is a real toggle on the local selection. */}
        <DropdownMenuLabel className="!bg-transparent !px-3 !pb-2 !pt-1.5">
          <div className="flex items-center justify-between gap-2">
            <div
              className="flex min-w-0 cursor-pointer items-center gap-2"
              onClick={toggleSelectAll}
            >
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                readOnly
                wrapperClassName="flex-shrink-0 pointer-events-none"
              />
              <span className="truncate text-sm font-semibold text-neutral-800">
                All
              </span>
            </div>
            <button
              type="button"
              onClick={resetSelection}
              disabled={!isDirty}
              className={cn(
                'text-sm font-medium transition-colors',
                isDirty
                  ? 'text-brand-active hover:text-brand-bright'
                  : 'cursor-not-allowed text-neutral-400'
              )}
            >
              Reset
            </button>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={ds2026DropdownSeparatorClass} />
        <div className="px-1.5 pb-1.5">
          <QuickFilterSearch
            value={search}
            onChange={setSearch}
            placeholder="Search owners"
          />
        </div>
        {/* Pinned shortcut: scope to just the current user in one click. Hidden
            while searching so it doesn't sit above unrelated matches. */}
        {myId && !search_ && (
          <>
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              onClick={toggleMine}
              className={cn(ds2026DropdownItemClass, '!gap-2.5')}
            >
              <Checkbox
                checked={isMineSelected}
                readOnly
                wrapperClassName="flex-shrink-0 pointer-events-none"
              />
              <span className="truncate font-medium">My deals only</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className={ds2026DropdownSeparatorClass} />
          </>
        )}
        {visibleOwners.length === 0 ? (
          <p className="px-3 py-2 text-sm text-fg-secondary">No owners found</p>
        ) : (
          visibleOwners.map((o) => {
            const id = String(o.value)
            return (
              <DropdownMenuItem
                key={id}
                // Keep the menu open so several owners can be toggled in a row.
                onSelect={(e) => e.preventDefault()}
                onClick={() => toggleOwner(id)}
                className={cn(ds2026DropdownItemClass, '!gap-2.5')}
              >
                <Checkbox
                  checked={selected.has(id)}
                  readOnly
                  wrapperClassName="flex-shrink-0 pointer-events-none"
                />
                <span className="truncate">{o.label}</span>
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
